import { useEffect, useMemo } from "react";
import type { TellescopeFormFieldBaseProps } from "../../types";
import { Session } from "@tellescope/sdk";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

export interface AppointmentBookingFieldContentProps
  extends Omit<TellescopeFormFieldBaseProps<"Appointment Booking">, "title"> {
  session: Session;
  enduserId: string;
  userIds?: string[];
  portalURL?: string;
  organizationSubdomain?: string;
}

export const AppointmentBookingFieldContent = ({
  value,
  onChange,
  className,
  session,
  enduserId,
  userIds = [],
  portalURL,
  organizationSubdomain,
  options,
}: AppointmentBookingFieldContentProps) => {
  const { t } = useTranslation("forms");
  const isUsingStaging = session.host.includes("staging");
  // Generate auth token using TanStack Query
  const {
    data: authTokenData,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "endusers",
    method: "generate_auth_token",
    request: {
      id: enduserId,
      overrideConsent: true,
      overrideOTP: true,
    },
    session,
  });

  // Derive the portal URL based on provided props
  const baseURL = useMemo(() => {
    if (portalURL) return portalURL;
    if (organizationSubdomain)
      return `https://${organizationSubdomain}.${isUsingStaging ? "staging-" : ""}portal.tellescope.com`;
    return `https://${isUsingStaging ? "staging-" : ""}portal.tellescope.com`; // fallback
  }, [portalURL, organizationSubdomain, isUsingStaging]);

  // Derive iframe URL from query data
  const iframeURL = useMemo(() => {
    if (!authTokenData?.authToken) return "";

    const userIdsParam = userIds.length > 0 ? userIds.join(",") : "";
    const scheduledByParam = session.userInfo?.id || "";

    return `${baseURL}/book-an-appointment/${options?.bookingPageId ?? ""}?token=${
      authTokenData.authToken
    }&embedded=true&userIds=${userIdsParam}&scheduledBy=${scheduledByParam}`;
  }, [authTokenData?.authToken, baseURL, options?.bookingPageId, userIds, session.userInfo?.id]);

  // Listen for postMessage from iframe indicating successful booking
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify message is from booking iframe
      if (event.data?.type === "Booking Success" && typeof event.data?.bookedEventId === "string") {
        // Update form value with the booked calendar event ID
        onChange(event.data.bookedEventId);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onChange]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <p>{t("Loading appointment booking...")}</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={className} style={{ padding: "2rem", color: "red" }}>
        <p>
          {t("Error: {{errorMessage}}", {
            errorMessage: error instanceof Error ? error.message : t("Failed to load appointment booking"),
          })}
        </p>
      </div>
    );
  }

  // Show success message if appointment was booked
  if (value) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ marginBottom: "1rem", color: "green", fontWeight: "bold" }}>
          {t("✓ Appointment Successfully Booked")}
        </div>
        <p style={{ fontSize: "0.875rem", color: "#666" }}>{t("Event ID: {{id}}", { id: value })}</p>
      </div>
    );
  }

  // Show iframe when URL is ready
  if (iframeURL) {
    return (
      <div className={className}>
        <iframe
          src={iframeURL}
          title="Appointment Booking"
          style={{
            width: "100%",
            height: "800px",
            scale: "0.85",
            border: "none",
          }}
        />
      </div>
    );
  }

  return null;
};
